import { API_BASE_URL } from '../config/api';
import { logger } from '../utils/logger';

export interface LiveKitRoomToken {
  success: boolean;
  token: string;
  wsUrl: string;
  roomName: string;
  participantName: string;
  expiresAt: string;
  roomType?: string;
  coinUsage?: {
    featureId: string;
    cost: number;
    remainingBalance: number;
  };
  error?: string;
  details?: string;
}

export interface LiveKitRoom {
  name: string;
  sid: string;
  numParticipants: number;
  maxParticipants: number;
  creationTime: string;
  metadata: any;
}

export interface LiveKitParticipant {
  identity: string;
  name: string;
  state: string;
  joinedAt: string;
  metadata: any;
}

class LiveKitService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${API_BASE_URL}/api/livekit`;
  }

  /**
   * Get authorization headers with JWT token
   */
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  }

  /**
   * Create a new LiveKit room
   */
  async createRoom(options: {
    roomName: string;
    roomType: 'personal_chat' | 'group_compatibility' | 'voice_consultation';
    conversationId?: string;
    groupId?: string;
    consultationId?: string;
    participants?: string[];
    maxParticipants?: number;
    validFor?: string;
  }): Promise<LiveKitRoomToken> {
    try {
      logger.info('Creating LiveKit room', options);

      const response = await fetch(`${this.baseUrl}/create-room`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(options),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create room');
      }

      logger.info('LiveKit room created successfully', {
        roomName: data.roomName,
        roomType: data.roomType,
      });

      return data;
    } catch (error) {
      logger.error('Error creating LiveKit room:', error);
      return {
        success: false,
        token: '',
        wsUrl: '',
        roomName: options.roomName,
        participantName: '',
        expiresAt: '',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Join an existing room
   */
  async joinRoom(options: {
    roomName: string;
    participantName?: string;
    canPublish?: boolean;
    canSubscribe?: boolean;
    canPublishData?: boolean;
    validFor?: string;
  }): Promise<LiveKitRoomToken> {
    try {
      logger.info('Joining LiveKit room', options);

      const response = await fetch(`${this.baseUrl}/join-room`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(options),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to join room');
      }

      logger.info('Successfully joined LiveKit room', {
        roomName: data.roomName,
      });

      return data;
    } catch (error) {
      logger.error('Error joining LiveKit room:', error);
      return {
        success: false,
        token: '',
        wsUrl: '',
        roomName: options.roomName,
        participantName: options.participantName || '',
        expiresAt: '',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Create a quick personal chat room
   */
  async createPersonalChatRoom(conversationId: string): Promise<LiveKitRoomToken> {
    try {
      logger.info('Creating quick personal chat room', { conversationId });

      const response = await fetch(`${this.baseUrl}/quick-personal-chat`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ conversationId }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create personal chat room');
      }

      logger.info('Personal chat room created successfully', {
        roomName: data.roomName,
        conversationId,
      });

      return data;
    } catch (error) {
      logger.error('Error creating personal chat room:', error);
      return {
        success: false,
        token: '',
        wsUrl: '',
        roomName: '',
        participantName: '',
        expiresAt: '',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * List active rooms
   */
  async listRooms(): Promise<{
    success: boolean;
    rooms: LiveKitRoom[];
    totalRooms: number;
    error?: string;
  }> {
    try {
      logger.info('Listing LiveKit rooms');

      const response = await fetch(`${this.baseUrl}/rooms`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to list rooms');
      }

      logger.info('Successfully listed LiveKit rooms', {
        totalRooms: data.totalRooms,
      });

      return data;
    } catch (error) {
      logger.error('Error listing LiveKit rooms:', error);
      return {
        success: false,
        rooms: [],
        totalRooms: 0,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Get participants in a room
   */
  async getRoomParticipants(roomName: string): Promise<{
    success: boolean;
    participants: LiveKitParticipant[];
    roomName: string;
    error?: string;
  }> {
    try {
      logger.info('Getting room participants', { roomName });

      const response = await fetch(`${this.baseUrl}/room/${encodeURIComponent(roomName)}/participants`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to get room participants');
      }

      logger.info('Successfully got room participants', {
        roomName,
        participantCount: data.participants.length,
      });

      return data;
    } catch (error) {
      logger.error('Error getting room participants:', error);
      return {
        success: false,
        participants: [],
        roomName,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * End a room session
   */
  async endRoom(roomName: string): Promise<{
    success: boolean;
    message?: string;
    roomName: string;
    error?: string;
  }> {
    try {
      logger.info('Ending LiveKit room', { roomName });

      const response = await fetch(`${this.baseUrl}/room/${encodeURIComponent(roomName)}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to end room');
      }

      logger.info('Successfully ended LiveKit room', { roomName });

      return data;
    } catch (error) {
      logger.error('Error ending LiveKit room:', error);
      return {
        success: false,
        roomName,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Send data message to room
   */
  async sendDataToRoom(
    roomName: string,
    message: string,
    destinationSids?: string[]
  ): Promise<{
    success: boolean;
    message?: string;
    roomName: string;
    dataLength?: number;
    error?: string;
  }> {
    try {
      logger.info('Sending data to LiveKit room', { roomName, messageLength: message.length });

      const response = await fetch(`${this.baseUrl}/room/${encodeURIComponent(roomName)}/send-data`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          message,
          destinationSids: destinationSids || [],
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send data to room');
      }

      logger.info('Successfully sent data to LiveKit room', {
        roomName,
        dataLength: data.dataLength,
      });

      return data;
    } catch (error) {
      logger.error('Error sending data to LiveKit room:', error);
      return {
        success: false,
        roomName,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Check LiveKit service health
   */
  async healthCheck(): Promise<{
    success: boolean;
    status: string;
    wsUrl?: string;
    timestamp?: string;
    error?: string;
  }> {
    try {
      logger.info('Checking LiveKit service health');

      const response = await fetch(`${this.baseUrl}/health`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Health check failed');
      }

      logger.info('LiveKit service health check completed', {
        status: data.status,
      });

      return data;
    } catch (error) {
      logger.error('Error checking LiveKit service health:', error);
      return {
        success: false,
        status: 'unhealthy',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Utility method to generate room name for personal chat
   */
  generatePersonalChatRoomName(conversationId: string): string {
    return `personal_chat_${conversationId}`;
  }

  /**
   * Utility method to generate room name for group compatibility
   */
  generateGroupChatRoomName(groupId: string): string {
    return `group_compat_${groupId}`;
  }

  /**
   * Utility method to generate room name for voice consultation
   */
  generateVoiceConsultationRoomName(consultationId: string): string {
    return `voice_consult_${consultationId}`;
  }
}

// Export singleton instance
export const livekitService = new LiveKitService();
export default livekitService;
